import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ExtractionStage } from '../../../api/listeners/document-extraction'
import { normalizeRecognizedListenerAddress } from '../../../api/listeners/recognized-address'
import { Button } from '../../ui/button'
import { registrationAddresses } from './registration-address'
import css from './ui.recognition.module.scss'

type Props = {
    stage: ExtractionStage | null
    registrationAddress: string
    onRegistrationAddress: (value: string, expectedAddress: string) => boolean
    onPendingChange?: (pending: boolean) => void
}

const reasons = {
    ambiguous: 'Адресные подсказки нашли несколько подходящих адресов. Уточните адрес в «Основных данных».',
    not_found: 'Адрес не найден в адресных подсказках. Уточните его в «Основных данных».',
    missing_postal_code: 'Для распознанного адреса не найден почтовый индекс. Уточните адрес в «Основных данных».',
    incomplete: 'Не удалось подтвердить полный адрес с домом, корпусом и квартирой. Уточните его в «Основных данных».',
    unavailable: 'Адресные подсказки временно недоступны. Повторите нормализацию адреса.',
    normalized: '',
}

export const RecognizedRegistrationAddress = ({ stage, registrationAddress, onRegistrationAddress, onPendingChange }: Props) => {
    const baseline = useRef(registrationAddress)
    const apply = useRef(onRegistrationAddress)
    apply.current = onRegistrationAddress
    const pending = useRef(false), pendingCallback = useRef(onPendingChange)
    pendingCallback.current = onPendingChange
    const reportPending = useCallback((value: boolean) => {
        pending.current = value
        pendingCallback.current?.(value)
    }, [])
    const { addresses, needsReview } = useMemo(() => registrationAddresses(stage), [stage])
    const [selected, setSelected] = useState<number | null>(null)
    const [retry, setRetry] = useState(0)
    const [state, setState] = useState<{ phase: 'idle' | 'loading' | 'applied' | 'review'; message: string; address?: string; retryable?: boolean }>({ phase: 'idle', message: '' })
    const completedRequest = useRef('')
    const index = selected ?? (addresses.length === 1 && !needsReview ? 0 : null)
    const address = index === null ? undefined : addresses[index]
    const addressKey = address ? JSON.stringify(address) : ''

    useEffect(() => {
        if (!addressKey) { reportPending(false); return }
        const address = JSON.parse(addressKey) as typeof addresses[number]
        const requestKey = JSON.stringify([stage?.stageID, addressKey, retry])
        if (completedRequest.current === requestKey) { reportPending(false); return }
        const controller = new AbortController()
        reportPending(true)
        setState({ phase: 'loading', message: 'Нормализуем адрес регистрации и определяем почтовый индекс…' })
        void normalizeRecognizedListenerAddress(address, controller.signal).then(response => {
            if (controller.signal.aborted) return
            completedRequest.current = requestKey
            const data = response.data
            if (response.status !== 200 || !data) {
                setState({ phase: 'review', message: reasons.unavailable, retryable: true })
                return
            }
            const value = data.normalizedAddress?.trim()
            if (data.reason !== 'normalized' || !value || !/^\d{6},\s+\S/.test(value) || value.length > 300) {
                setState({ phase: 'review', message: reasons[data.reason] || reasons.incomplete, retryable: data.reason === 'unavailable' })
                return
            }
            if (!apply.current(value, baseline.current)) {
                setState({ phase: 'review', message: 'Адрес не перенесён: анкета или её адрес уже изменены. Проверьте поле в «Основных данных».' })
                return
            }
            setState({ phase: 'applied', message: 'Адрес перенесён в «Адрес прописки слушателя». Изменение сохранится вместе с анкетой.', address: value })
        }).catch(() => {
            if (!controller.signal.aborted) {
                completedRequest.current = requestKey
                setState({ phase: 'review', message: reasons.unavailable, retryable: true })
            }
        }).finally(() => {
            if (!controller.signal.aborted) reportPending(false)
        })
        return () => { controller.abort(); reportPending(false) }
    }, [addressKey, stage?.stageID, retry, reportPending])

    useEffect(() => { onPendingChange?.(pending.current) }, [stage, onPendingChange])

    if (!addresses.length && !needsReview) return null
    return <section className={css.group} aria-label="Перенос адреса регистрации">
        <h5>Адрес прописки слушателя</h5>
        {index === null ? <>
            <p className={css.warning}>{addresses.length
                ? 'В документах несколько адресов или есть неоднозначная регистрация. Выберите адрес для переноса после проверки оригинала.'
                : 'Адрес регистрации требует проверки. Уточните адрес в «Основных данных».'}</p>
            {addresses.map((item, itemIndex) => <p key={JSON.stringify(item)}><Button onClick={() => setSelected(itemIndex)}>Перенести адрес: {item.query}</Button></p>)}
        </> : <>
            <p role="status" aria-live="polite" className={state.phase === 'review' ? css.warning : undefined}>{state.message}</p>
            {state.address && <p>{state.address}</p>}
            {state.retryable && <Button onClick={() => setRetry(value => value + 1)}>Повторить нормализацию адреса</Button>}
            {state.phase === 'review' && addresses.length > 1 && <Button onClick={() => {
                setSelected(null)
                setState({ phase: 'idle', message: '' })
                setRetry(value => value + 1)
            }}>Выбрать другой адрес</Button>}
        </>}
    </section>
}
